import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AngularEditorComponent, AngularEditorConfig } from '@kolkov/angular-editor';
import { saveAs } from 'file-saver';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Mail } from 'src/app/core/_models/mail';
import { UserMail } from 'src/app/core/_models/userMail';
import { MailService } from 'src/app/core/_services/mail.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { environment } from 'src/environments/environment';
import { DialogEmailStatusComponent } from '../shared/dialog-email-status/dialog-email-status.component';

interface ILabelValuePair {
  label: string;
  value: string;
}

@Component({
  selector: 'app-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss']
})
export class EmailComponent implements OnInit, AfterViewInit {
  @ViewChild(AngularEditorComponent) private editor?: AngularEditorComponent;

  form: FormGroup;
  allRecipients: UserMail[] = [];
  visibleRecipients: UserMail[] = [];
  selectedEmails = new Set<string>();
  allVisibleSelected = false;
  someVisibleSelected = false;
  search = '';
  submitted = false;
  private profilesByEmail = new Map<string, Set<string>>();

  get formCtls() {
    return this.form.controls;
  }

  get chosenRecipients(): UserMail[] {
    return this.formCtls.allUsers.value ? this.allRecipients : this.allRecipients.filter((r) => this.selectedEmails.has(r.email!));
  }

  get canExportCsv() {
    return this.chosenRecipients.length > 0;
  }

  profilesFilter: ILabelValuePair[] = [
    { label: 'ACD', value: 'ACD' },
    { label: 'ACEU', value: 'ACEU' },
    { label: 'ACU', value: 'ACU' },
    { label: 'ABE', value: 'ABE' },
    { label: 'BE', value: 'BE' },
    { label: 'AI', value: 'AI' },
    { label: 'CI', value: 'CI' }
  ];

  // Les images (insérées en base64, bloquées par Gmail), les vidéos (iframe) et le choix de police ne passent pas dans un mail.
  htmlEditorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    minHeight: '220px',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    placeholder: '',
    toolbarHiddenButtons: [
      ['subscript', 'superscript', 'fontName'],
      ['customClasses', 'insertImage', 'insertVideo', 'toggleEditorMode']
    ]
  };

  constructor(
    private formBuilder: FormBuilder,
    private mailService: MailService,
    private spinnerService: SpinnerOverlayService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {
    this.form = this.formBuilder?.group({
      allUsers: [true],
      profile: [null],
      sender: [environment.adminMail],
      subject: [],
      mailContent: []
    });
  }

  ngOnInit(): void {
    this.translate.get('email_placeholder_text').subscribe((placeholder) => {
      this.htmlEditorConfig = { ...this.htmlEditorConfig, placeholder };
    });

    this.spinnerService.show();

    this.mailService.getListMails().subscribe(
      (users) => {
        if (users) {
          this.loadRecipients(users);
        }
        this.spinnerService.hide();
      },
      () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  ngAfterViewInit(): void {
    const toolbar = this.editor?.editorToolbar;
    if (!toolbar) {
      return;
    }
    // Les libellés de l'éditeur sont en anglais et en dur. On réécrit ceux des options gardées sans recréer les objets :
    // l'option déjà sélectionnée est l'un d'eux, et prend ainsi son libellé traduit.
    this.translate.get(['email_editor_normal_text', 'email_editor_heading_1', 'email_editor_heading_2', 'email_editor_heading_3']).subscribe((t) => {
      const labels: Record<string, string> = {
        default: t.email_editor_normal_text,
        h1: t.email_editor_heading_1,
        h2: t.email_editor_heading_2,
        h3: t.email_editor_heading_3
      };
      toolbar.headings = Object.keys(labels).map((value) => {
        const option = toolbar.headings.find((h) => h.value === value)!;
        option.label = labels[value];
        return option;
      });
    });
    // L'éditeur écrit des <font size="1"> à "7">, que les navigateurs et les clients mail rendent dans ces tailles.
    const pixelSizes: Record<string, string> = { 1: '10', 2: '13', 3: '16', 4: '18', 5: '24', 6: '32', 7: '48' };
    toolbar.fontSizes.forEach((size) => (size.label = `${pixelSizes[size.value]} px`));
    // L'éditeur reconnaît aussi p, div et pre, et remonte au-delà de sa zone de saisie jusqu'aux div de la page :
    // tout ce qui n'est pas un titre proposé s'affiche comme du texte normal.
    const triggerBlocks = toolbar.triggerBlocks.bind(toolbar);
    toolbar.triggerBlocks = (nodes: Node[]) => {
      triggerBlocks(nodes);
      if (!['h1', 'h2', 'h3'].includes(toolbar.block)) {
        toolbar.block = 'default';
      }
    };
  }

  private loadRecipients(users: UserMail[]): void {
    const byEmail = new Map<string, UserMail>();
    users
      .filter((u) => !!u.email)
      .forEach((u) => {
        const email = u.email!.trim();
        const key = email.toLowerCase();
        if (!byEmail.has(key)) {
          byEmail.set(key, { email, firstName: u.firstName, lastName: u.lastName });
          this.profilesByEmail.set(email, new Set<string>());
        }
        if (u.profilCode) {
          this.profilesByEmail.get(byEmail.get(key)!.email!)!.add(u.profilCode);
        }
      });
    byEmail.forEach((r) => (r.profilCode = [...this.profilesByEmail.get(r.email!)!].join(', ')));
    this.allRecipients = [...byEmail.values()].sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''));
    this.applyFilters();
  }

  applyFilters(): void {
    const profile: string | null = this.formCtls.profile.value;
    const search = this.search.trim().toLowerCase();
    this.visibleRecipients = this.allRecipients.filter((r) => (!profile || this.profilesByEmail.get(r.email!)?.has(profile)) && (!search || `${r.firstName} ${r.lastName} ${r.email}`.toLowerCase().includes(search)));
    this.refreshSelectionState();
  }

  toggleRecipient(email: string, checked: boolean): void {
    if (checked) {
      this.selectedEmails.add(email);
    } else {
      this.selectedEmails.delete(email);
    }
    this.refreshSelectionState();
  }

  toggleVisible(checked: boolean): void {
    this.visibleRecipients.forEach((r) => (checked ? this.selectedEmails.add(r.email!) : this.selectedEmails.delete(r.email!)));
    this.refreshSelectionState();
  }

  private refreshSelectionState(): void {
    const selectedCount = this.visibleRecipients.filter((r) => this.selectedEmails.has(r.email!)).length;
    this.allVisibleSelected = selectedCount > 0 && selectedCount === this.visibleRecipients.length;
    this.someVisibleSelected = selectedCount > 0 && !this.allVisibleSelected;
  }

  sendMail(): void {
    this.submitted = true;
    if (this.form.invalid || this.chosenRecipients.length === 0) {
      this.form.markAllAsTouched();
      return;
    }
    const formData = this.form.value;

    const newMail: Mail = {
      To: this.chosenRecipients.map((r) => r.email!),
      From: formData.sender,
      Subject: formData.subject,
      Body: formData.mailContent,
      IsHtml: true
    };

    this.mailService.sendMailToTable(newMail).subscribe(() => {
      this.snackbarService.open(SnackbarTypes.mailSend);
      this.resetForm();
      this.getEmails();
    });
  }

  private resetForm(): void {
    this.form.reset({ allUsers: true, profile: null, sender: environment.adminMail, subject: null, mailContent: '' });
    this.selectedEmails.clear();
    this.search = '';
    this.submitted = false;
    this.applyFilters();
  }

  getEmails(): void {
    this.mailService.getSentMails().subscribe({
      next: (res) => {
        this.dialog.open(DialogEmailStatusComponent, {
          data: { emailData: res },
          autoFocus: false
        });
      }
    });
  }

  exportUsersMails() {
    if (this.canExportCsv) this.saveToCsv(this.chosenRecipients);
  }

  private saveToCsv(values: any[]) {
    const csvUsers = values.map((v) => {
      return {
        lastName: v.lastName,
        firstName: v.firstName,
        email: v.email
      };
    });

    var csvData = this.convertToCSV(csvUsers);
    var a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    var blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv; charset=utf-18' });
    saveAs(blob, this.translate.instant('email_csv_filename'));
  }

  convertToCSV(objArray: any): string {
    var array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
    var str = '';
    var row = '';

    row += this.translate.instant('email_csv_lastname');
    row += this.translate.instant('email_csv_firstname');
    row += this.translate.instant('email_csv_email');

    row = row.slice(0, -1);
    str += row + '\r\n';

    for (var i = 0; i < array.length; i++) {
      var line = '';
      for (var index in array[i]) {
        if (line != '') line += ';';

        line += array[i][index];
      }
      str += line + '\r\n';
    }
    return str;
  }
}
